<template>
  <ImpactPopover
    v-if="partnerScoreBucket"
    :icon="isRecommended ? ['fak', 'trophy'] : ['fak', 'partner-score']"
    :title="tooltipTitle"
    :bar-class="tooltipBarColor"
    :icon-class="`${tooltipIconColor} w-12 h-12`"
    :text-class="tooltipTextColor"
  >
    <section v-if="phrases.length > 0">
      <ul class="list-none text-neutral-text">
        <li
          v-for="{
            metricLabel,
            key,
            formattedValue,
            comparisonText,
          } in phrases"
          :key
          class="flex items-top gap-6 ml-6"
        >
          <div>•</div>
          <div>
            Your {{ metricLabel }} with {{ partnerName }}'s customers is
            <strong>{{ formattedValue }}</strong
            ><template v-if="comparisonText"
              >. This is <strong>{{ comparisonText }}</strong> your average
              across partners</template
            >.
          </div>
        </li>
        <li v-if="coveragePercentage" class="flex items-top gap-6 ml-6">
          <div>•</div>
          <div>
            <strong>{{ coveragePercentage }}</strong> of your total overlap pool
            comes from accounts you share with {{ partnerName }}.
          </div>
        </li>
      </ul>
    </section>
  </ImpactPopover>
</template>

<script setup lang="ts">
import { capitalize } from 'lodash';
import { computed } from 'vue';

import useAttribution from '@/composables/useAttribution';
import usePartnerImpact from '@/composables/usePartnerImpact';
import { PARTNER_SCORE_COLORS } from '@/constants/partners';
import type { Partner, PartnerTrimmed } from '@/types/partners';

import ImpactPopover from '../ImpactPopover.vue';

const { partner, isRecommended = false } = defineProps<{
  partner: Partner | PartnerTrimmed;
  isRecommended?: boolean;
}>();

const { computePhrases } = usePartnerImpact();
const { percentFormatter } = useAttribution();

const partnerName = partner.name;
const metrics = partner.metrics;

const partnerScoreBucket = computed(() => metrics?.partner_score_bucket);
const coveragePercentage = computed(() =>
  metrics?.coverage_percentage
    ? percentFormatter({
        value: metrics?.coverage_percentage,
        fractionDigits: 0,
      })
    : undefined,
);

const phrases = computed(() => computePhrases(metrics, true));

const tooltipTitle = computed(() => {
  if (!partnerScoreBucket.value) return '';
  if (isRecommended) return 'Recommended Partner';
  return `Partner Score: ${capitalize(partnerScoreBucket.value)}`;
});

const partnerScoreColor = computed(() =>
  partnerScoreBucket.value
    ? PARTNER_SCORE_COLORS[partnerScoreBucket.value]
    : null,
);
const tooltipTextColor = computed(() =>
  isRecommended ? 'text-[#4D3C1A]' : `text-${partnerScoreColor.value}-text`,
);
const tooltipIconColor = computed(() =>
  isRecommended ? 'text-recommended' : `text-${partnerScoreColor.value}-accent`,
);
const tooltipBarColor = computed(() =>
  isRecommended ? 'bg-recommended' : `bg-${partnerScoreColor.value}-accent`,
);
</script>
